import { animate, state, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { afterNextRender, Component, computed, effect, ElementRef, inject, Injector, input, OnDestroy, output, QueryList, signal, untracked, ViewChild, ViewChildren } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AccordionModule } from 'primeng/accordion';
import { TooltipModule } from 'primeng/tooltip';
import { interval, map } from 'rxjs';
import { ButtonComponent } from 'src/app/@core/components/ui/button/button.component';
import { IconComponent } from 'src/app/@core/components/ui/icon/icon.component';
import { environment } from 'src/environments/environment.dev';
import { TOOLS } from '../../../constants/tool.constants';
import { MESSAGE_TYPE, MessageItem, THREAD_METADATA_STATUS } from '../../../models/model';
import { MESSAGE_FEEDBACK } from '../../../models/types';
import { SidePanelService } from '../../../services/side-panel.service';
import { getSubagentTaskId, ThreadMessagesService, ToolGroupItem } from '../../../services/thread-messages.service';
import { ToolCallPanelService } from '../../../services/tool-call-panel.service';
import { copyCanvasContent, getArtifactToolData, getArtifactToolStatus, getToolName, getToolTitle, isSummarizationMessage } from '../../../utils/utils';
import { CanvasComponent } from '../../tool-views/canvas/canvas.component';
import { BlobLoaderComponent } from '../../ui/blob-loader/blob-loader.component';
import { AiMessageComponent } from '../messages/ai-message/ai-message.component';
import { HumanMessageComponent } from '../messages/human-message/human-message.component';
import { TOOL_STATUS } from '../../../enums/enum';
import { SVG_ICONS } from 'src/app/contract-management/constants/constant';
import { ThreadStateIndicatorComponent } from '../../ui/thread-state-indicator/thread-state-indicator.component';

// Removed ToolGroup interface as we now use ToolGroupItem from ThreadMessagesService

// Render windowing: long threads render only their latest turns; older turns are added on demand.
const INITIAL_TURNS = 3;
const INITIAL_ITEMS = 60;
const GROW_TURNS = 3;
const GROW_ITEMS = 80;
/** Scrolling up within this distance of the top loads earlier turns. */
const LOAD_EARLIER_THRESHOLD_PX = 400;

function isHumanItem(item: MessageItem | ToolGroupItem): boolean {
    return !(item as ToolGroupItem).isToolGroup && (item as MessageItem).type === MESSAGE_TYPE.HUMAN;
}

/**
 * Returns the grouped-list index where the window should start so that it covers the turns
 * ending at `fromIndex`. Always takes at least one whole turn, then adds earlier turns while
 * staying within `maxTurns` and `maxItems`. Cuts only at human items (turn starts), so a tool
 * or task group is never split. Items before the first human message are never left hidden on
 * their own.
 */
function findTurnStart(items: (MessageItem | ToolGroupItem)[], fromIndex: number, maxTurns: number, maxItems: number): number {
    let start = fromIndex;
    let turns = 0;
    for (let i = fromIndex - 1; i >= 0; i--) {
        if (!isHumanItem(items[i])) continue;
        if (turns > 0 && fromIndex - i > maxItems) break;
        start = i;
        turns++;
        if (turns >= maxTurns) break;
    }
    if (turns === 0) return 0;
    for (let i = start - 1; i >= 0; i--) {
        if (isHumanItem(items[i])) return start;
    }
    return 0;
}

@Component({
    selector: 'app-message-list',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        AccordionModule,
        TooltipModule,
        HumanMessageComponent,
        AiMessageComponent,
        BlobLoaderComponent,
        IconComponent,
        ButtonComponent,
        CanvasComponent,
        ThreadStateIndicatorComponent,
    ],
    templateUrl: './message-list.component.html',
    styleUrl: './message-list.component.scss',
    animations: [
        trigger('fadeIn', [
            state('void', style({ opacity: 0 })),
            state('*', style({ opacity: 1 })),
            transition('void => *', animate('400ms ease-in-out'))
        ])
    ],
    host: {
        'class': 'block w-full h-full'
    }
})
export class MessageListComponent implements OnDestroy {
    @ViewChildren(CanvasComponent) canvasComponents?: QueryList<CanvasComponent>;

    // Inputs
    isOwner = input<boolean>(false);
    /** Forwarded to thread-state-indicator so action CTAs are hidden for view-only shared users. */
    canEdit = input<boolean>(true);
    /** True on the public "Anyone with the link" share route — restricts opening user-uploaded input files. */
    isPublicView = input<boolean>(false);
    /** When true (e.g. document-chat embed), tool accordions and rows use no gray background. */
    isEmbedded = input<boolean>(false);
    isSummarizing = input<boolean>(false);
    /** Task IDs of subagents currently summarizing. */
    summarizingTaskIds = input<string[]>([]);
    threadMetadataId = input<string>();
    startTimer = input<boolean>(false);
    pauseTimer = input<boolean>(false);

    // State
    showScrollButton = signal<boolean>(false);
    isScrolling = signal<boolean>(false);
    private scrollingTimeout?: ReturnType<typeof setTimeout>;
    /** Guards the one-time "scroll to latest generated document" on thread open. */
    private initialScrollDone = false;
    /** Tracks the last message's id so we can detect a freshly-appended user message. */
    private lastMessageId: string | null = null;
    private lastScrollTop = 0;
    /** True from the moment earlier turns are requested until they have rendered. */
    isLoadingEarlier = signal<boolean>(false);
    private injector = inject(Injector);

    // Outputs
    editMessage = output<{ messageId: string, content: string }>();
    feedback = output<{ messageId: string, feedback: MESSAGE_FEEDBACK }>();
    openImagePreview = output<string>();
    openCanvasPanel = output<{ message: MessageItem, isEditable: boolean }>();
    downloadCanvas = output<MessageItem>();
    scroll = output<Event>();
    approvePlan = output<void>();
    rejectPlan = output<void>();
    /** Emitted when user clicks "Save as prompt" on an AI message. Carries the message text. */
    saveAsPrompt = output<string>();
    /** Emitted when user clicks "Save to Library" on a human message's Enhanced Prompt accordion. */
    saveEnhancement = output<{ messageId: string; markdown: string }>();
    /** Forwarded when the user edits and approves the plan (steps or todos). */
    editPlan = output<string>();
    /** Forwarded when the user requests an AI revision of the plan with freeform feedback. */
    revisePlan = output<string>();
    timerExpired = output<void>();
    continueAfterError = output<void>();
    continueAfterPause = output<void>();
    /** Forwarded when the user picks an existing saved prompt from the reuse card. */
    useExistingPrompt = output<string>();
    /** Forwarded when the user wants a freshly AI-generated enhancement. */
    generateNewPrompt = output<void>();

    TOOL_STATUS = TOOL_STATUS;
    icons = SVG_ICONS;
    // View Child
    @ViewChild('scrollContainer') scrollContainer?: ElementRef<HTMLDivElement>;

    // Services
    threadMessagesService = inject(ThreadMessagesService);
    sidePanelService = inject(SidePanelService);
    toolCallPanelService = inject(ToolCallPanelService);

    // Constants
    MESSAGE_TYPE = MESSAGE_TYPE;
    THREAD_METADATA_STATUS = THREAD_METADATA_STATUS;
    MESSAGE_FEEDBACK = MESSAGE_FEEDBACK;
    TOOLS = TOOLS;

    // Dots for "Constructing..." animation
    dots$ = interval(800).pipe(map(i => '.'.repeat(i % 4)));

    // Render window
    /**
     * Id of the grouped item the render window starts at. Pinned when a thread loads so that
     * streamed/appended items never move the start; it only moves back via loadEarlier().
     * When it is null or no longer present (thread switch, edit truncation) the window falls
     * back to the latest turns.
     */
    private windowAnchorId = signal<string | null>(null);

    /** Index in groupedMessages() of the first rendered item. */
    windowStart = computed(() => {
        const grouped = this.threadMessagesService.groupedMessages();
        const anchorId = this.windowAnchorId();
        if (anchorId) {
            const anchorIndex = grouped.findIndex(item => item.id === anchorId);
            if (anchorIndex >= 0) return anchorIndex;
        }
        return findTurnStart(grouped, grouped.length, INITIAL_TURNS, INITIAL_ITEMS);
    });

    visibleItems = computed(() => this.threadMessagesService.groupedMessages().slice(this.windowStart()));

    hiddenTurnCount = computed(() =>
        this.threadMessagesService.groupedMessages().slice(0, this.windowStart()).filter(isHumanItem).length
    );

    // Accordion state, keyed by tool group id (user-toggled open/closed)
    accordionStateByGroupId: Record<string, number | null> = {};

    // Collapsed state for task (subagent) cards — keyed by a unique string ID
    collapsedTaskCards: Record<string, boolean> = {};

    copyIcons = signal<Record<string, string>>({});

    isTaskCardCollapsed(id: string): boolean {
        return this.collapsedTaskCards[id] === true;
    }

    toggleTaskCard(id: string): void {
        this.collapsedTaskCards[id] = !this.collapsedTaskCards[id];
    }

    // Tool Helper Wrappers
    getArtifactToolStatus = (m: MessageItem) => getArtifactToolStatus(m, this.threadMessagesService.messages());
    // Use getToolName with allMessages so that tool results get the correct icon/name
    getArtifactToolIcon = (m: MessageItem) => getArtifactToolData(getToolName(m, this.threadMessagesService.messages()), m, this.getArtifactToolStatus(m), this.threadMessagesService.messages());
    getArtifactFormattedToolName = (m: MessageItem) => getArtifactToolData(getToolName(m, this.threadMessagesService.messages()), m, this.getArtifactToolStatus(m), this.threadMessagesService.messages()).name;

    getArtifactStatusIcon(message: MessageItem): string {
        const status = this.getArtifactToolStatus(message);
        switch (status) {
            case TOOL_STATUS.LOADING: return 'loader-circle';
            case TOOL_STATUS.SUCCESS: return 'circle-check';
            case TOOL_STATUS.ERROR: return 'triangle-alert';
            default: return 'loader-circle';
        }
    }

    getArtifactStatusIconClass(message: MessageItem): string {
        const status = this.getArtifactToolStatus(message);
        switch (status) {
            case TOOL_STATUS.LOADING: return 'text-blue-500 animate-spin';
            case TOOL_STATUS.SUCCESS: return 'text-green-500';
            case TOOL_STATUS.ERROR: return 'text-amber-500';
            default: return 'text-blue-500 animate-spin';
        }
    }

    constructor() {
        effect(() => {
            const messages = this.threadMessagesService.messages();
            if (messages.length === 0) {
                // Thread switched / cleared — re-arm the initial document scroll.
                this.initialScrollDone = false;
                this.lastMessageId = null;
                this.lastScrollTop = 0;
                this.windowAnchorId.set(null);
                this.isLoadingEarlier.set(false);
                this.accordionStateByGroupId = {};
                return;
            }

            // Pin the render window on first load, or re-pin it if its anchor disappeared.
            untracked(() => {
                const grouped = this.threadMessagesService.groupedMessages();
                const anchorId = this.windowAnchorId();
                if (!anchorId || !grouped.some(item => item.id === anchorId)) {
                    this.windowAnchorId.set(grouped[this.windowStart()]?.id ?? null);
                }
            });

            const lastMessage = messages[messages.length - 1];
            const isNewHumanMessage = this.initialScrollDone && lastMessage.type === MESSAGE_TYPE.HUMAN && lastMessage.id !== this.lastMessageId;

            this.lastMessageId = lastMessage.id;
            requestAnimationFrame(() => {
                if (!this.initialScrollDone && this.scrollContainer) {
                    this.initialScrollDone = true;
                    this.performInitialScroll();
                } else if (isNewHumanMessage) {
                    this.scrollToBottom();
                } else if (this.initialScrollDone) {
                    this.scrollToBottomIfNear();
                }
            });
        });

        // Auto-scroll streaming args containers to bottom as content streams in
        effect(() => {
            const streamingTCs = this.threadMessagesService.streamingToolCalls();
            if (Object.keys(streamingTCs).length > 0) {
                requestAnimationFrame(() => {
                    document.querySelectorAll('.streaming-args-container').forEach(el => {
                        el.scrollTop = el.scrollHeight;
                    });
                });
            }
        });
    }

    ngAfterViewInit() {
        // If messages are already present when the view initializes, perform the
        // one-time initial scroll here (the messages effect handles the async-load case).
        if (!this.initialScrollDone && this.scrollContainer && this.threadMessagesService.messages().length > 0) {
            this.initialScrollDone = true;
            this.performInitialScroll();
        }
    }

    ngOnDestroy() {
        if (this.scrollingTimeout) {
            clearTimeout(this.scrollingTimeout);
        }
    }

    // Scroll Methods
    onScroll(event: Event) {
        this.scroll.emit(event);

        if (this.scrollContainer) {
            const element = this.scrollContainer.nativeElement;
            const atBottom = Math.abs(element.scrollHeight - element.scrollTop - element.clientHeight) < 100;
            this.showScrollButton.set(!atBottom);

            // Load earlier turns when the user scrolls up near the top.
            const scrollingUp = element.scrollTop < this.lastScrollTop;
            this.lastScrollTop = element.scrollTop;
            if (this.initialScrollDone && scrollingUp && element.scrollTop < LOAD_EARLIER_THRESHOLD_PX) {
                this.loadEarlier();
            }
        }

        // Show scrollbar thumb while scrolling, fade out after 1.5s of inactivity
        this.isScrolling.set(true);
        if (this.scrollingTimeout) {
            clearTimeout(this.scrollingTimeout);
        }
        this.scrollingTimeout = setTimeout(() => {
            this.isScrolling.set(false);
        }, 1500);
    }

    scrollToBottom(behavior: ScrollBehavior = 'smooth') {
        if (this.scrollContainer) {
            requestAnimationFrame(() => {
                const element = this.scrollContainer!.nativeElement;
                element.scrollTo({ top: element.scrollHeight, behavior });
            });
        }
    }

    /**
     * Extends the render window back by a few turns, keeping the viewport anchored on the
     * item that was first before the extension.
     */
    loadEarlier() {
        if (this.windowStart() === 0 || this.isLoadingEarlier()) return;
        this.isLoadingEarlier.set(true);

        // Rendering the earlier turns blocks the main thread, so let the loader paint first:
        // rAF runs just before the next paint, the timeout just after it.
        requestAnimationFrame(() => setTimeout(() => this.renderEarlierTurns()));
    }

    private renderEarlierTurns() {
        const start = this.windowStart();
        if (start === 0) {
            this.isLoadingEarlier.set(false);
            return;
        }

        const container = this.scrollContainer?.nativeElement;
        const anchorElement = container?.querySelector<HTMLElement>('.message-item');
        const anchorTop = container && anchorElement
            ? anchorElement.getBoundingClientRect().top - container.getBoundingClientRect().top
            : null;

        const grouped = this.threadMessagesService.groupedMessages();
        // Both changes land in the same render, so the anchor measurement below already
        // accounts for the loader being swapped back to the button (or removed).
        this.windowAnchorId.set(grouped[findTurnStart(grouped, start, GROW_TURNS, GROW_ITEMS)].id);
        this.isLoadingEarlier.set(false);

        afterNextRender(() => {
            if (!container || !anchorElement || anchorTop === null || !anchorElement.isConnected) return;
            const newTop = anchorElement.getBoundingClientRect().top - container.getBoundingClientRect().top;
            // 'instant' overrides the container's CSS scroll-behavior: smooth.
            container.scrollTo({ top: container.scrollTop + (newTop - anchorTop), behavior: 'instant' });
        }, { injector: this.injector });
    }

    /**
     * One-time scroll performed when a thread is opened.
     * If the thread contains a generated document (the "Presented Files" attachment
     * group rendered on an AI message), scroll so the latest such document sits near
     * the top of the viewport. Otherwise fall back to scrolling to the bottom.
     */
    private performInitialScroll() {
        if (!this.scrollContainer) return;
        const container = this.scrollContainer.nativeElement;

        requestAnimationFrame(() => {
            const documents = container.querySelectorAll<HTMLElement>('.js-generated-document');
            // 'instant' rather than smooth: a smooth scroll emits a burst of scroll events,
            // each triggering change detection.
            if (documents.length === 0) {
                this.scrollToBottom('instant');
                return;
            }

            // The last document in DOM order is the most recently generated one.
            const latestDocument = documents[documents.length - 1];
            const containerTop = container.getBoundingClientRect().top;
            const documentTop = latestDocument.getBoundingClientRect().top;
            const offset = 16; // small breathing room above the document
            const targetScrollTop = container.scrollTop + (documentTop - containerTop) - offset;

            container.scrollTo({ top: Math.max(0, targetScrollTop), behavior: 'instant' });
        });
    }

    /** Scroll to bottom only when the user is already within 150px of the bottom. */
    private scrollToBottomIfNear() {
        if (!this.scrollContainer) return;
        const element = this.scrollContainer.nativeElement;
        const distanceFromBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
        if (distanceFromBottom < 150) {
            element.scrollTo({ top: element.scrollHeight, behavior: 'smooth' });
        }
    }

    // Utilities
    getAssetUrl(imagePath: string): string {
        return `${environment.s3Url}/inndoc-ai/assets/images/${imagePath}`;
    }

    // Logic from ThreadContentComponent
    shouldShowBobAvatar(index: number, messageType: string): boolean {
        const messages = this.threadMessagesService.groupedMessages();
        if (messageType === MESSAGE_TYPE.HUMAN) return false;
        if (index === 0) return true;

        const previousMessage: any = messages[index - 1];
        const isPreviousHuman = (!previousMessage.isToolGroup && previousMessage.type === MESSAGE_TYPE.HUMAN) ||
            (!previousMessage.isToolGroup && previousMessage.type === MESSAGE_TYPE.SYSTEM && index > 1 && !(messages[index - 2] as any).isToolGroup && (messages[index - 2] as MessageItem).type === MESSAGE_TYPE.HUMAN);
        return isPreviousHuman;
    }

    showLoadingBob(index: number, messageType: string): boolean {
        const messages = this.threadMessagesService.groupedMessages();
        const isLastMessage = index === messages.length - 1;
        const isAIMessage = messageType === MESSAGE_TYPE.AI || messageType === MESSAGE_TYPE.TOOL;

        if (this.threadMessagesService.threadMetadataStatus() !== THREAD_METADATA_STATUS.IN_PROGRESS || !isLastMessage || !isAIMessage) {
            return false;
        }
        return true;
    }

    hasAIResponseStarted(): boolean {
        const messages = this.threadMessagesService.messages();
        if (messages.length === 0) return false;
        let lastHumanIndex = -1;
        for (let i = messages.length - 1; i >= 0; i--) {
            if (messages[i].type === MESSAGE_TYPE.HUMAN) {
                lastHumanIndex = i;
                break;
            }
        }

        for (let i = lastHumanIndex + 1; i < messages.length; i++) {
            if (messages[i].type === MESSAGE_TYPE.AI) return true;
        }
        return false;
    }

    asToolGroup(item: any): ToolGroupItem | null {
        return item && item.isToolGroup ? (item as ToolGroupItem) : null;
    }

    asMessage(item: any): MessageItem | null {
        return item && !item.isToolGroup ? (item as MessageItem) : null;
    }

    showArtifact(message: MessageItem): boolean {
        // Use getToolName with allMessages to handle tool results without direct name property
        const toolName = getToolName(message, this.threadMessagesService.messages());

        // Filter out canvas tools or other tools we don't want to show as standard cards
        return !!toolName && ![TOOLS.WRITE_TODOS].includes(toolName as TOOLS);
    }

    hasVisibleContent(item: MessageItem | ToolGroupItem): boolean {
        if ((item as any).isToolGroup) return true;
        const message = item as MessageItem;
        if (message.type === MESSAGE_TYPE.HUMAN) return true;
        if (message.type === MESSAGE_TYPE.AI || message.type === MESSAGE_TYPE.TOOL) {
            return !!(
                this.isSummarizationMessage(message) ||
                (message.content && message.content.trim()) ||
                this.getImageUrl(message) ||
                this.getCanvasDetails(message).canvasContent
            );
        }
        return false;
    }

    isMultipleToolCalls(group: ToolGroupItem): boolean {
        return group.messages.length > 1;
    }

    isTaskGroup(group: ToolGroupItem): boolean {
        return !!(group?.isTask);
    }

    isTaskToolMessage(message: MessageItem): boolean {
        return getToolName(message, this.threadMessagesService.messages()) === TOOLS.TASK;
    }

    // Canvas
    getCanvasDetails(item: MessageItem): {
        canvasName: string;
        canvasContent: string;
        isFirstVersion: boolean;
        isLatestVersion: boolean;
    } {
        if (item?.type === MESSAGE_TYPE.TOOL && item['response_metadata']?.isCanvas) {
            return item['additional_kwargs']
        }
        return {
            canvasName: '',
            canvasContent: '',
            isFirstVersion: false,
            isLatestVersion: false
        }
    }

    getCopyIcon(messageId: string): string {
        return this.copyIcons()[messageId] || 'copy';
    }

    getImageUrl(message: MessageItem): string | null {
        if (message.type !== MESSAGE_TYPE.TOOL) return null;
        return message['response_metadata']?.imageUrl || message.additional_kwargs?.imageUrl || null;
    }

    copyCanvas(message: MessageItem) {
        if (!this.canvasComponents) {
            return;
        }
        const isCopySuccess = copyCanvasContent(this.canvasComponents, message);
        if (isCopySuccess) {
            // Set copy state for this specific message
            this.copyIcons.update(icons => ({ ...icons, [message.id]: 'check-check' }));
            setTimeout(() => {
                // Reset copy state for this specific message after 2 seconds
                this.copyIcons.update(icons => {
                    const updated = { ...icons };
                    delete updated[message.id];
                    return updated;
                });
            }, 2000);
        }
    }

    onDownloadCanvas(message: MessageItem) {
        this.downloadCanvas.emit(message);
    }

    editCanvasContent(message: MessageItem, isEditable: boolean = true) {
        this.openCanvasPanel.emit({ message, isEditable });
    }

    onImagePreviewClick(url: string) {
        if (url) this.openImagePreview.emit(url);
    }

    // Methods passed to children
    onEditMessage(event: { messageId: string, content: string }) {
        this.editMessage.emit(event);
    }

    onFeedback(event: MESSAGE_FEEDBACK, messageId: string) {
        this.feedback.emit({ messageId, feedback: event });
    }

    onArtifactClick(message: MessageItem) {
        // Compute the status for the tool
        const computedStatus = this.getArtifactToolStatus(message);

        // Use openWithChainItem like the old implementation
        this.toolCallPanelService.openWithChainItem(message, computedStatus);
    }

    hasFeedback(messageId: string): { like: boolean, dislike: boolean } {
        const feedbacks = this.threadMessagesService.messageFeedbacksByMessageId();
        const feedback = feedbacks[messageId];
        return {
            like: feedback?.feedback === MESSAGE_FEEDBACK.LIKE,
            dislike: feedback?.feedback === MESSAGE_FEEDBACK.DISLIKE
        };
    }

    getToolMessages(group: ToolGroupItem): MessageItem[] {
        return group.messages.filter(m => m.type === 'tool');
    }

    isPrivateToolMessage(message: MessageItem): boolean {
        return !!message.additional_kwargs?.['isPrivate'];
    }

    getPrivateToolLabel(message: MessageItem): string {
        return message.additional_kwargs?.['privateToolLabel'] ?? 'Processing';
    }

    isAllPrivateGroup(group: ToolGroupItem): boolean {
        const toolMessages = this.getToolMessages(group);
        return toolMessages.length > 0 && toolMessages.every(m => this.isPrivateToolMessage(m));
    }

    isTaskCompleted(group: ToolGroupItem): boolean {
        return !!(group.isTask && group.taskResult?.content?.trim());
    }

    getToolGroupStatus(group: ToolGroupItem): TOOL_STATUS {
        // For task groups, derive status from the task result
        if (group.isTask) {
            if (this.isTaskCompleted(group)) return TOOL_STATUS.SUCCESS;
            // If no task result yet, check if any nested tool errored
            const nested = group.nestedGroupedMessages ?? [];
            const hasNestedError = nested.some(item => {
                if ((item as ToolGroupItem).isToolGroup) {
                    return this.getToolGroupStatus(item as ToolGroupItem) === TOOL_STATUS.ERROR;
                }
                return false;
            });
            return hasNestedError ? TOOL_STATUS.ERROR : TOOL_STATUS.LOADING;
        }

        const toolMessages = this.getToolMessages(group);
        if (toolMessages.length === 0) return TOOL_STATUS.LOADING;

        const statuses = toolMessages.map(m => this.getArtifactToolStatus(m));
        if (statuses.includes(TOOL_STATUS.LOADING)) return TOOL_STATUS.LOADING;
        if (statuses.includes(TOOL_STATUS.ERROR)) return TOOL_STATUS.ERROR;
        return TOOL_STATUS.SUCCESS;
    }

    getToolGroupStatusIcon(group: ToolGroupItem): string {
        const status = this.getToolGroupStatus(group);
        return status === TOOL_STATUS.SUCCESS ? 'circle-check' : (status === TOOL_STATUS.ERROR ? 'triangle-alert' : 'loader-circle');
    }

    getToolGroupStatusIconClass(group: ToolGroupItem): string {
        const status = this.getToolGroupStatus(group);
        switch (status) {
            case TOOL_STATUS.LOADING:
                return 'text-blue-500 animate-spin';
            case TOOL_STATUS.SUCCESS:
                return 'text-green-500';
            case TOOL_STATUS.ERROR:
                return 'text-amber-500';
            default:
                return 'text-blue-500 animate-spin';
        }
    }

    getToolGroupTitle(group: ToolGroupItem): string {
        if (this.isTaskGroup(group)) {
            // Extract subagent_type from the task AI message's tool call args
            const messages = this.threadMessagesService.messages();
            const taskAiMsg = group.messages.find(
                m => m.type === MESSAGE_TYPE.AI && m.tool_calls?.some((tc: any) => tc.name === TOOLS.TASK)
            );
            let subagentType = '';
            if (taskAiMsg) {
                const taskCall = taskAiMsg.tool_calls?.find((tc: any) => tc.name === TOOLS.TASK);
                subagentType = taskCall?.args?.subagent_type || '';
            }
            if (subagentType) {
                return subagentType.split('_').map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
            }
            return 'Sub Agent';
        }

        const toolMessages = this.getToolMessages(group);
        if (toolMessages.length === 0) return 'Analyzing...';

        // All-private group: use private label for single tool, "Ran N tools" for multiple
        if (this.isAllPrivateGroup(group)) {
            if (toolMessages.length === 1) {
                return this.getPrivateToolLabel(toolMessages[0]);
            }
            const verb = this.isToolGroupStreaming(group) ? 'Running' : 'Ran';
            return `${verb} ${toolMessages.length} tools`;
        }

        if (this.isToolGroupStreaming(group)) {
            if (toolMessages.length === 1) {
                const name = getToolName(toolMessages[0], this.threadMessagesService.messages());
                return name ? getToolTitle(name) : 'Analyzing...';
            }
            return `Running ${toolMessages.length} tools`;
        }

        if (toolMessages.length === 1) {
            return this.getArtifactFormattedToolName(toolMessages[0]);
        }
        return `Ran ${toolMessages.length} tools`;
    }

    isToolGroupStreaming(group: ToolGroupItem): boolean {
        const streamingTCs = this.threadMessagesService.streamingToolCalls();
        if (Object.keys(streamingTCs).length === 0) return false;
        return group.messages.some(m =>
            m.tool_call_id && !!streamingTCs[m.tool_call_id]
        );
    }

    isToolCallStreaming(message: MessageItem): boolean {
        if (!message.tool_call_id) return false;
        const streamingTCs = this.threadMessagesService.streamingToolCalls();
        return !!streamingTCs[message.tool_call_id];
    }

    getStreamingArgs(message: MessageItem): string {
        if (!message.tool_call_id) return '';
        const streamingTCs = this.threadMessagesService.streamingToolCalls();
        return streamingTCs[message.tool_call_id]?.args || '';
    }

    getStreamingToolTitle(message: MessageItem): string {
        const name = getToolName(message, this.threadMessagesService.messages());
        return name ? getToolTitle(name) : 'Tool';
    }

    /** Recursively collect all tool MessageItems from a nested grouped message list. */
    private collectToolMessagesFromGrouped(items: (MessageItem | ToolGroupItem)[]): MessageItem[] {
        const result: MessageItem[] = [];
        for (const item of items) {
            if ((item as ToolGroupItem).isToolGroup) {
                const grp = item as ToolGroupItem;
                if (grp.isTask) {
                    // For nested task groups recurse into their nested messages
                    result.push(...this.collectToolMessagesFromGrouped(grp.nestedGroupedMessages ?? []));
                } else {
                    result.push(...this.getToolMessages(grp));
                }
            } else {
                const msg = item as MessageItem;
                if (msg.type === 'tool') result.push(msg);
            }
        }
        return result;
    }

    getToolGroupProgress(group: ToolGroupItem) {
        if (group.isTask) {
            if (this.isTaskCompleted(group)) {
                const allTools = this.collectToolMessagesFromGrouped(group.nestedGroupedMessages ?? []);
                const total = Math.max(allTools.length, 1);
                return { total, completed: total };
            }
            const allTools = this.collectToolMessagesFromGrouped(group.nestedGroupedMessages ?? []);
            const total = Math.max(allTools.length, 1);
            const completed = allTools.filter(m => this.getArtifactToolStatus(m) !== 'loading').length;
            return { total, completed };
        }
        const toolMessages = this.getToolMessages(group);
        const total = toolMessages.length;
        const completed = toolMessages.filter(m => this.getArtifactToolStatus(m) !== 'loading').length;
        return { total, completed };
    }

    getAccordionState(index: number, toolGroup?: ToolGroupItem) {
        if (toolGroup && this.accordionStateByGroupId[toolGroup.id] !== undefined) {
            return this.accordionStateByGroupId[toolGroup.id] ?? -1;
        }

        if (toolGroup) {
            const groupedMessages = this.threadMessagesService.groupedMessages();
            const isLastItem = index === groupedMessages.length - 1;
            const isThreadInProgress = this.threadMessagesService.threadMetadataStatus() === THREAD_METADATA_STATUS.IN_PROGRESS;
            const status = this.getToolGroupStatus(toolGroup);

            // Keep accordion open if it's actively loading OR if it's the last item in an active thread (waiting for potential next tool calls in the same group)
            if (status === TOOL_STATUS.LOADING || (isLastItem && isThreadInProgress)) {
                return 0;
            }
            return -1;
        }

        return -1;
    }

    /**
     * Returns staggered CSS animation delay for message items on initial load.
     * Caps at ~300ms so long threads don't feel sluggish.
     */
    getMessageAnimationDelay(index: number): string {
        const delay = Math.min(index * 30, 300);
        return `${delay}ms`;
    }

    /**
     * Checks if a message is a summarization message
     * @param message - The message to check
     * @returns true if the message is from summarization
     */
    isSummarizationMessage(message: MessageItem): boolean {
        return isSummarizationMessage(message);
    }

    /** True if this task group is currently in summarizing state (show compacting UI inside card). */
    isTaskSummarizing(group: ToolGroupItem): boolean {
        const summarizingIds = this.summarizingTaskIds();
        if (!summarizingIds.length || !group.isTask) return false;
        for (const m of group.messages) {
            const ns = m.additional_kwargs?.['namespace'];
            const id = getSubagentTaskId(ns);
            if (id && summarizingIds.includes(id)) return true;
        }
        return false;
    }

    /** Main-thread summarizing: show top-level compacting UI. */
    isMainThreadSummarizing(): boolean {
        return this.isSummarizing();
    }
}
